import { ActionDataWidgets } from '../types'
import { DebouncedState, useDebouncedCallback } from 'use-debounce'
import { FormProps } from '../components/Layout'
import { LinkProvider } from '@dtx-company/inter-app/src/constants/linkTypes'
import { MaskType } from '@app/common/src/components/ImageEditor/ImageEditor.types'
import { NFTFormFields } from './types'
import { UseFormMethods, useForm } from 'react-hook-form-deprecated'
import { addProtocol } from '../../../components/profile/LinkForm/utils'
import { defaultWidgetLink } from '../../../constants'
import { getNFTDetail, openSeaRegex } from './utils'
import { getValidatedActionData } from '../typeUtils'
import { logger } from '@dtx-company/logger'
import { setPreviewLink } from '@dtx-company/inter-app/src/redux/slices/linkEditor/linkEditorSlice'
import { useDeleteLinkWithAutosave } from '../deleteUtils'
import { useDispatch } from 'react-redux'
import { useEffect, useMemo, useState } from 'react'
import { useSubmitWidget } from '../submitUtils'
import { v4 as uuid } from 'uuid'

export type NFTFormState = {
  formTitle: string
  formValues: {
    image: string
    link: string
  }
  handleSubmit: () => void
  deleteNFTLink: () => void
  errors: UseFormMethods['errors']
  fetchNFTDetails: DebouncedState<(_val: string) => Promise<void>>
  loadingNFTDetails: boolean
}

export type UseNFTFormStateProps = Pick<FormProps, 'curr' | 'order' | 'handleClose'>

export const useNFTFormState = ({
  curr,
  order,
  handleClose
}: UseNFTFormStateProps): NFTFormState => {
  const id = curr ? curr.id : uuid()
  const widgetType: ActionDataWidgets = 'nft'
  const dispatch = useDispatch()
  const actionData = getValidatedActionData<'nft'>(curr?.actionData, 'link')
  const submitWidget = useSubmitWidget()
  const {
    register,
    errors,
    setValue: setFormValue,
    handleSubmit,
    watch
  } = useForm({
    defaultValues: {
      image: actionData?.image ?? '',
      link: actionData?.link ?? ''
    }
  })
  const deleteLink = useDeleteLinkWithAutosave()
  const formValues = watch()
  const [loadingNFTDetails, setLoadingNFTDetails] = useState(false)
  const onSubmit = async ({
    image,
    maskType,
    link
  }: {
    link: string
    maskType: MaskType
    image: string
  }): Promise<void> => {
    await submitWidget({
      curr,
      image,
      widgetType,
      actionData: {
        maskType,
        link: addProtocol(link),
        image
      },
      fields: {
        id,
        order
      }
    })
    handleClose()
  }

  const deleteNFTLink = (): void => {
    deleteLink({
      linkId: id,
      linkOrder: order
    })

    handleClose()
  }

  const previewLink = useMemo(
    () => ({
      ...defaultWidgetLink,
      id,
      order,
      provider: LinkProvider.WIDGET,
      type: widgetType,
      actionData: {
        image: formValues.image
      }
    }),
    [formValues.image, id, order]
  )

  const updatePreview = useDebouncedCallback(() => {
    dispatch(
      setPreviewLink({
        previewLink
      })
    )
  }, 500)

  const fetchNFTDetails = useDebouncedCallback(async (val: string): Promise<void> => {
    if (!val) return
    setLoadingNFTDetails(true)
    setFormValue(NFTFormFields.link, val)

    try {
      const nftDetail = await getNFTDetail(val)
      if (nftDetail && nftDetail?.assets?.length > 0 && nftDetail?.assets[0]?.image_url) {
        setFormValue(NFTFormFields.image, nftDetail.assets[0]?.image_url)
      } else {
        setFormValue(NFTFormFields.image, '')
      }
      updatePreview()
    } catch (err) {
      logger.logError(err, { team: 'templates' })
    } finally {
      setLoadingNFTDetails(false)
    }
  }, 500)

  useEffect(() => {
    register(NFTFormFields.link, {
      validate: value => (openSeaRegex.test(value) ? true : 'Please enter a valid opensea link'),
      required: true
    })
    register(NFTFormFields.image)
  }, [register])

  return {
    formTitle: `${curr ? 'Edit this' : 'Add an'} Opensea NFT`,
    formValues: formValues,
    errors,
    handleSubmit: handleSubmit(onSubmit),
    deleteNFTLink,
    fetchNFTDetails,
    loadingNFTDetails
  }
}
